import { useEffect, useRef, useState } from "react";
import { useDebounce } from "~/lib/hooks/useDebounce";
import {
  PLACES_AUTOCOMPLETE_DEBOUNCE_MS,
  PLACES_AUTOCOMPLETE_MIN_CHARS,
  shouldFetchPlacesAutocomplete,
} from "~/lib/places-autocomplete-constants";
import {
  createPlacesSessionToken,
} from "~/lib/places-session";
import type { MapsCallSource } from "~/lib/maps/attribution";
import { PlaceDetails } from "~/lib/server-functions/get-place-detail";
import { PlacePrediction } from "~/lib/server-functions/get-places-autocomplete";
import { usePlaceDetails } from "~/lib/tanstack-query/hooks/usePlaceDetails";
import { usePlacesAutocomplete } from "~/lib/tanstack-query/hooks/usePlacesAutocomplete";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "../ui/command";
import { Input } from "../ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "../ui/popover";
import { InfoIcon, Search } from "lucide-react";
import { Label } from "../ui/label";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";

export const LocationSearchInput = ({
  label,
  placeholder = "Suche deine Location über die automatische Maps-Suche.",
  source,
  onChange,
}: {
  label?: string;
  placeholder?: string;
  /** Callsite for Google cost attribution. */
  source: MapsCallSource;
  /**
   * Receives the resolved Place Details together with the `placeId` they came
   * from, so callers can persist the Place ID alongside the label instead of
   * forcing a later Geocoding call.
   */
  onChange: (
    details: PlaceDetails | null,
    meta: { placeId: string | null },
  ) => void;
}) => {
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);
  const [detailsSessionToken, setDetailsSessionToken] = useState<
    string | undefined
  >();
  const inputRef = useRef<HTMLInputElement>(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const placesSessionTokenRef = useRef<string | null>(null);
  // Survives the `selectedPlaceId` reset so `onChange` can report the Place ID
  // the details were resolved from.
  const resolvedPlaceIdRef = useRef<string | null>(null);

  const debouncedInputValue = useDebounce(
    inputValue,
    PLACES_AUTOCOMPLETE_DEBOUNCE_MS,
  );

  // Only make API call when input is focused and user has interacted.
  // Focus/interaction are state (not refs) so blur/focus re-render and flip
  // `enabled` before a later refetch can issue an Autocomplete request.
  const shouldFetchSuggestions = shouldFetchPlacesAutocomplete(
    isInputFocused,
    hasUserInteracted,
  );
  // Gate fetching, not the query key: a blank key drops the suggestion list on
  // the next render, which can unmount an item the user is mid-click on.
  const { data: autocompleteData, isFetching: isPendingSuggestions } =
    usePlacesAutocomplete(debouncedInputValue, {
      source,
      sessionToken: placesSessionTokenRef.current ?? undefined,
      profile: source === "location_form" ? "address" : "regions",
      enabled: shouldFetchSuggestions,
    });
  const suggestions = autocompleteData?.predictions ?? [];
  // Google only opened a billing session when it actually served this request.
  const googleSawAutocompleteSession =
    autocompleteData?.sessionEligible === true;

  // Fetch place details when a placeId is selected. Pass the Autocomplete
  // session token only for live (non-cached) suggestions so Google can bill a
  // session; cached suggestions must use uncached-token Details so our DB/
  // memory Place Details cache still applies.
  const { data: placeDetails } = usePlaceDetails(selectedPlaceId || "", {
    source,
    sessionToken: detailsSessionToken,
  });

  // Call onChange when place details are available
  useEffect(() => {
    if (placeDetails) {
      onChange?.(placeDetails, { placeId: resolvedPlaceIdRef.current });
      setSelectedPlaceId(null);
      setDetailsSessionToken(undefined);
      placesSessionTokenRef.current = null;
    }
  }, [placeDetails, onChange]);

  // Open autocomplete when suggestions arrive (after debounce completes)
  // Only if user has interacted and input is focused
  useEffect(() => {
    if (!isInputFocused || !hasUserInteracted) {
      return;
    }

    if (debouncedInputValue.length < PLACES_AUTOCOMPLETE_MIN_CHARS) {
      setAutocompleteOpen(false);
      setSelectedIndex(-1);
      return;
    }

    // Open autocomplete when we have suggestions or are loading them
    if (suggestions.length > 0 || isPendingSuggestions) {
      setAutocompleteOpen(true);
      setSelectedIndex(-1); // Reset selection when suggestions change
    }
  }, [
    suggestions,
    debouncedInputValue,
    isPendingSuggestions,
    isInputFocused,
    hasUserInteracted,
  ]);

  const handleLocationChange = (value: string) => {
    setHasUserInteracted(true);
    setInputValue(value);
    // Reset placeId when user types (clears selection)
    if (selectedPlaceId) {
      setSelectedPlaceId(null);
      setDetailsSessionToken(undefined);
      resolvedPlaceIdRef.current = null;
      onChange?.(null, { placeId: null });
    }
    // Close autocomplete if input is too short
    if (value.length < PLACES_AUTOCOMPLETE_MIN_CHARS) {
      setAutocompleteOpen(false);
      setSelectedIndex(-1);
    }
    // Note: Autocomplete will open automatically when suggestions arrive (via useEffect)
  };

  const handleSelectPlace = (suggestion: PlacePrediction) => {
    // Same token on Place Details only when Google saw this Autocomplete session.
    const sessionToken = googleSawAutocompleteSession
      ? (placesSessionTokenRef.current ?? undefined)
      : undefined;
    setDetailsSessionToken(sessionToken);
    placesSessionTokenRef.current = null;

    setInputValue(suggestion.description);
    setHasUserInteracted(false);
    resolvedPlaceIdRef.current = suggestion.placeId;
    setSelectedPlaceId(suggestion.placeId);
    setAutocompleteOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!autocompleteOpen || suggestions.length === 0) {
      // Allow normal input behavior when autocomplete is closed
      return;
    }

    // Handle arrow keys to navigate suggestions
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev > 0 ? prev - 1 : suggestions.length - 1,
      );
    }
    // Handle Enter to select the highlighted suggestion
    else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
        handleSelectPlace(suggestions[selectedIndex]);
      } else if (suggestions.length > 0) {
        // If no item is selected, select the first one
        handleSelectPlace(suggestions[0]);
      }
    }
    // Handle Escape to close
    else if (e.key === "Escape") {
      e.preventDefault();
      setAutocompleteOpen(false);
      setSelectedIndex(-1);
    }
  };

  return (
    <>
      <Alert variant={"moderate"}>
        <InfoIcon className="h-4 w-4" />
        <AlertTitle>Hinweis</AlertTitle>
        <AlertDescription>
          Beginne mit der Eingabe der Adresse und wähle den passenden Vorschlag
          aus der Liste. Die Felder darunter werden automatisch ausgefüllt.
        </AlertDescription>
      </Alert>
      <Popover open={autocompleteOpen} onOpenChange={setAutocompleteOpen}>
        <PopoverAnchor asChild>
          <div className="flex flex-col gap-2">
            {label && <Label>{label}</Label>}
            <Input
              ref={inputRef}
              placeholder={placeholder}
              required={true}
              className="w-full"
              iconStart={<Search />}
              name="location"
              value={inputValue}
              disablePasswordManagers={true}
              onChange={(e) => handleLocationChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                setIsInputFocused(true);
                // A prefilled selection is not a new Autocomplete intent.
                setHasUserInteracted(false);
                if (!placesSessionTokenRef.current) {
                  placesSessionTokenRef.current = createPlacesSessionToken();
                }
                // Open autocomplete if we already have suggestions
                if (
                  inputValue.length >= PLACES_AUTOCOMPLETE_MIN_CHARS &&
                  suggestions.length > 0
                ) {
                  setAutocompleteOpen(true);
                }
              }}
              onBlur={() => {
                setIsInputFocused(false);
                setHasUserInteracted(false);
              }}
            />
          </div>
        </PopoverAnchor>
        <PopoverContent
          className="p-0"
          style={{ width: "var(--radix-popover-trigger-width)" }}
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => {
            setAutocompleteOpen(false);
            inputRef.current?.focus();
          }}
        >
          <Command
            shouldFilter={false}
            value={
              selectedIndex >= 0 && selectedIndex < suggestions.length
                ? suggestions[selectedIndex].description
                : undefined
            }
          >
            <CommandList>
              {isPendingSuggestions ? (
                <div className="py-6 text-center text-sm">Lade...</div>
              ) : suggestions.length === 0 ? (
                <CommandEmpty>Keine Ergebnisse gefunden.</CommandEmpty>
              ) : (
                <CommandGroup>
                  {suggestions.map((suggestion) => (
                    <CommandItem
                      key={suggestion.placeId}
                      value={suggestion.description}
                      onSelect={() => handleSelectPlace(suggestion)}
                      className="cursor-pointer"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {suggestion.mainText}
                        </span>
                        {suggestion.secondaryText && (
                          <span className="text-xs text-muted-foreground">
                            {suggestion.secondaryText}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
};
